import { useState } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import { useSettingsStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import type { Entity, CreateEntityInput, UpdateEntityInput } from '@perfin/shared';

interface EntityFormProps {
  entity: Entity | null;
  onClose: () => void;
}

export function EntityForm({ entity, onClose }: EntityFormProps) {
  const { addEntity, editEntity } = useSettingsStore();
  const toast = useToast();

  const [name, setName] = useState(entity?.name || '');
  const [saving, setSaving] = useState(false);

  const isEditing = !!entity;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Entity name is required');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        const input: UpdateEntityInput = { name: name.trim() };
        await editEntity(entity.id, input);
        toast.success('Entity updated');
      } else {
        const input: CreateEntityInput = { name: name.trim() };
        await addEntity(input);
        toast.success('Entity created');
      }
      onClose();
    } catch (error: unknown) {
      console.error('Entity error:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error(isEditing ? `Failed to update entity: ${message}` : `Failed to create entity: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEditing ? 'Edit Entity' : 'Add Entity'}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Entity Name */}
        <Input
          label="Entity Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Chase Bank, Amazon, Walmart"
          autoFocus
        />

        <p className="text-sm text-gray-500">
          An entity represents a bank, institution, or counterparty. You can then create accounts under each entity.
        </p>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            {isEditing ? 'Save Changes' : 'Add Entity'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
